import React from "react";
import Header from "../components/Header";
import BrandLogos from "../components/BrandLogos";
import VideoSection from "../components/VideoSection";
import WhoShouldAttend from "../components/WhoShouldAttend";
import WhyShouldAttend_Intl from "../components/WhyShouldAttend_Intl";
import WhyZyluOnly from "../components/WhyZyluOnly";
import MoneyBackGuarantee from "../components/MoneyBackGuarantee";
import CTAButton from "../components/CTAButton";
import PainPoints from "../components/PainPoints";
import Solution from "../components/Solution";
import Results from "../components/Results";
import Testimonials from "../components/Testimonials";
import BookingForm from "../components/BookingForm";
import { TrendingUp, DollarSign, BarChart3, Clock } from "lucide-react";
import { motion } from "framer-motion";
import AnimatedText from "../components/AnimatedText";
import StickyTimer from "../components/StickyTimer";

const RevenueLandingPage_Intl: React.FC = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <StickyTimer page="revenue" />

      {/* Hero Section */}
      <section className="relative bg-white text-black py-8 sm:py-12 md:py-16 lg:py-20 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-32 -right-32 w-72 h-72 bg-gradient-to-br from-red-200 to-red-300 rounded-full opacity-20 animate-float"></div>
          <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-gradient-to-tr from-black/5 to-gray-200 rounded-full opacity-25 animate-bounce-subtle"></div>
          <div className="absolute top-20 right-20 w-48 h-48 bg-gradient-to-r from-red-100 to-red-200 rounded-full opacity-30 animate-pulse"></div>
          <div
            className="absolute bottom-20 left-20 w-56 h-56 bg-gradient-to-l from-gray-100 to-gray-200 rounded-full opacity-20 animate-float"
            style={{ animationDelay: "1s" }}
          ></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="relative z-10 grid lg:grid-cols-2 gap-6 sm:gap-8 md:gap-10 lg:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="order-1 lg:order-1"
            >
              <AnimatedText
                text="Let's fix your"
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black leading-tight text-black-600"
                type="fade"
              />
              <AnimatedText
                text="salon growth"
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black leading-tight text-red-600 mb-3 sm:mb-4 md:mb-5 lg:mb-6"
                type="slide"
                delay={0.3}
              />
              <AnimatedText
                text="in 30 Minutes."
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black leading-tight text-red-600 mb-4 sm:mb-5 md:mb-6"
                type="slide"
                delay={0.3}
              />
              <AnimatedText
                text="Stop spending big money on fresha & zenoti"
                className="text-lg sm:text-xl md:text-2xl text-gray-700 mb-4 sm:mb-5 md:mb-6 font-semibold"
                type="fade"
                delay={0.5}
              />
              <AnimatedText
                text="🎯 Break free today — starting at just $19/month!"
                className="text-lg sm:text-xl md:text-2xl text-gray-600 mb-6 sm:mb-7 md:mb-8 font-medium"
                type="fade"
                delay={0.6}
              />

              <div className="flex flex-col md:flex-row gap-4 sm:gap-5 md:gap-6 mb-8 sm:mb-10 md:mb-12">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.9 }}
                >
                  <CTAButton
                    text="Book your free demo"
                    className="text-sm sm:text-base md:text-lg lg:text-xl py-3 sm:py-4 md:py-5 lg:py-6 px-4 sm:px-6 md:px-7 lg:px-8"
                  />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.1 }}
                  className="text-center md:text-left"
                >
                  <p className="text-sm sm:text-base md:text-lg font-semibold text-black mb-1 sm:mb-2">
                    ✓ 30-Minute Demo
                  </p>
                  <p className="text-sm sm:text-base md:text-lg font-semibold text-black">
                    ✓ Growth oriented software
                  </p>
                </motion.div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-5 lg:gap-6 order-2 lg:order-2 relative"
            >
              {/* Background Animation */}
              <div className="absolute inset-0 bg-red-50 rounded-2xl animate-pulse opacity-30 -z-10"></div>
              <div className="absolute -inset-4 bg-red-200 rounded-3xl blur-xl opacity-20 animate-bounce-subtle -z-10"></div>

              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-red-600 p-3 sm:p-4 md:p-5 lg:p-6 rounded-xl text-center text-white shadow-lg"
              >
                <TrendingUp className="h-6 w-6 sm:h-8 sm:w-8 md:h-10 md:w-10 lg:h-12 lg:w-12 mx-auto mb-2 sm:mb-3 md:mb-4" />
                <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2">40%+</div>
                <p className="text-xs sm:text-sm md:text-base font-semibold">Average Revenue Increase</p>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-white text-black p-3 sm:p-4 md:p-5 lg:p-6 rounded-xl text-center border-2 border-gray-200 shadow-lg"
              >
                <DollarSign className="h-6 w-6 sm:h-8 sm:w-8 md:h-10 md:w-10 lg:h-12 lg:w-12 mx-auto mb-2 sm:mb-3 md:mb-4 text-red-600" />
                <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-red-600 mb-1 sm:mb-2">2K+</div>
                <p className="text-xs sm:text-sm md:text-base font-semibold">Average Annual Savings</p>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-gray-100 p-3 sm:p-4 md:p-5 lg:p-6 rounded-xl text-center text-black shadow-lg"
              >
                <BarChart3 className="h-6 w-6 sm:h-8 sm:w-8 md:h-10 md:w-10 lg:h-12 lg:w-12 mx-auto mb-2 sm:mb-3 md:mb-4 text-black" />
                <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-black mb-1 sm:mb-2">2,000+</div>
                <p className="text-xs sm:text-sm md:text-base font-semibold">Successful Salons</p>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-red-600 p-3 sm:p-4 md:p-5 lg:p-6 rounded-xl text-center text-white shadow-lg"
              >
                <Clock className="h-6 w-6 sm:h-8 sm:w-8 md:h-10 md:w-10 lg:h-12 lg:w-12 mx-auto mb-2 sm:mb-3 md:mb-4 text-white-600" />
                <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2">24/7</div>
                <p className="text-xs sm:text-sm md:text-base font-semibold">Automated Bookings</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <BrandLogos />
      <VideoSection
        videoId="55f7eddc245447d080727917348500de"
        videoType="loom"
        title="See How to Boost Your Revenue by 40%+"
        description="Watch this exclusive demo showing exactly how Zylu helps salon owners boost their revenue"
      />
      <WhoShouldAttend />
      <WhyShouldAttend_Intl />
      <WhyZyluOnly />
      <PainPoints />
      <Solution />
      <Results />
      <Testimonials />
      <MoneyBackGuarantee />
      <BookingForm 
        selectedDate=""
        selectedTime=""
        onBookingSubmit={() => {}}
        onBack={() => {}}
      />
    </div>
  );
};

export default RevenueLandingPage_Intl;
